import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Trophy, Plus, CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dealTrackingService from '../../../services/dealTrackingService';

/**
 * Real World Results Tracker
 * Shows wins/losses, conversion rate, quick log button
 * Makes Lumo adaptive by capturing what actually works
 */

const RealWorldResults = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, dealsData] = await Promise.all([
        dealTrackingService.getUserStats(),
        dealTrackingService.getRecentDeals(3),
      ]);
      setStats(statsData);
      setRecentDeals(dealsData);
    } catch (error) {
      console.error('Error loading deal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLog = () => {
    // For now, just show a simple modal
    // Later can route to dedicated deal logging page
    setShowLogModal(true);
  };

  if (loading) {
    return (
      <div className="rounded-2xl bg-card p-6 shadow-lg border border-border animate-pulse dark:bg-slate-800 dark:border-slate-700">
        <div className="h-6 w-48 bg-muted rounded mb-4 dark:bg-slate-700" />
        <div className="h-24 bg-muted rounded dark:bg-slate-700" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-card p-6 shadow-lg border border-border hover:shadow-xl transition-shadow dark:bg-slate-800 dark:border-slate-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 p-2.5 shadow-lg">
            <Trophy className="w-full h-full text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Real-World Results</h3>
            <p className="text-sm text-muted-foreground">Track what's actually working</p>
          </div>
        </div>
        <button
          onClick={handleQuickLog}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium shadow-md hover:shadow-lg hover:scale-105 transition-all"
        >
          <Plus className="w-4 h-4" />
          Log Deal
        </button>
      </div>

      {/* Stats Grid */}
      {stats && (stats.totalWins > 0 || stats.totalLosses > 0) ? (
        <div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {/* Win Rate */}
            <div className="text-center p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
              <div className="text-2xl font-bold text-emerald-600 mb-1">
                {stats.winRatePercent}%
              </div>
              <div className="text-xs text-muted-foreground font-medium">Win Rate</div>
            </div>

            {/* Total Wins */}
            <div className="text-center p-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 dark:from-slate-700 dark:to-slate-700 dark:border-slate-600">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                {stats.totalWins}
              </div>
              <div className="text-xs text-muted-foreground font-medium">Deals Won</div>
            </div>

            {/* Revenue */}
            <div className="text-center p-4 rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 dark:from-slate-700 dark:to-slate-700 dark:border-slate-600">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                ${(stats.totalRevenue / 1000).toFixed(0)}k
              </div>
              <div className="text-xs text-muted-foreground font-medium">Revenue</div>
            </div>
          </div>

          {/* Recent Deals */}
          {recentDeals.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground mb-3">Recent Activity</h4>
              {recentDeals.map((deal) => (
                <div
                  key={deal.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted border border-border hover:bg-muted/60 transition-colors dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600"
                >
                  <div className="flex items-center gap-3">
                    {deal.status === 'won' && (
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    )}
                    {deal.status === 'lost' && (
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    )}
                    {deal.status === 'ongoing' && (
                      <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    )}
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {deal.dealName || 'Unnamed Deal'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(deal.outcomeDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                        {deal.dealValue && (
                          <span className="ml-2 text-muted-foreground">
                            ${deal.dealValue.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {deal.techniquesUsed && deal.techniquesUsed.length > 0 && (
                    <div className="flex gap-1">
                      {deal.techniquesUsed.slice(0, 2).map((tech, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700"
                        >
                          {tech}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-4 pt-4 border-t border-border dark:border-slate-700">
            <button
              onClick={() => navigate('/deals')}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              View All Deals
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        // Empty state
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center dark:bg-slate-700">
            <TrendingUp className="w-8 h-8 text-muted-foreground" />
          </div>
          <h4 className="text-base font-semibold text-foreground mb-2">
            Start Tracking Your Wins
          </h4>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
            Log your real-world deals to see what techniques actually work. Lumo learns from your wins to coach you better.
          </p>
          <button
            onClick={handleQuickLog}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium shadow-md hover:shadow-lg hover:scale-105 transition-all"
          >
            <Plus className="w-4 h-4" />
            Log Your First Deal
          </button>
        </div>
      )}

      {/* Quick Log Modal (simple version) */}
      {showLogModal && (
        <QuickLogModal onClose={() => setShowLogModal(false)} onSuccess={loadData} />
      )}
    </div>
  );
};

// Simple modal for quick deal logging
const QuickLogModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    dealName: '',
    status: 'won',
    dealValue: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await dealTrackingService.logDeal({
        dealName: formData.dealName,
        status: formData.status,
        dealValue: formData.dealValue ? parseFloat(formData.dealValue) : null,
        notes: formData.notes,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error logging deal:', error);
      alert('Failed to log deal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-2xl max-w-md w-full p-6 dark:bg-slate-800">
        <h3 className="text-xl font-bold text-foreground mb-4">Log a Deal</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Deal Name
            </label>
            <input
              type="text"
              value={formData.dealName}
              onChange={(e) => setFormData({ ...formData, dealName: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground dark:bg-slate-700 dark:border-slate-600"
              placeholder="e.g., Acme Corp - Enterprise Plan"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground dark:bg-slate-700 dark:border-slate-600"
            >
              <option value="won">Won 🎉</option>
              <option value="lost">Lost 😔</option>
              <option value="ongoing">Ongoing 🔄</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Deal Value (Optional)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-2.5 text-muted-foreground">$</span>
              <input
                type="number"
                value={formData.dealValue}
                onChange={(e) => setFormData({ ...formData, dealValue: e.target.value })}
                className="w-full pl-8 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground dark:bg-slate-700 dark:border-slate-600"
                placeholder="10000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground dark:bg-slate-700 dark:border-slate-600"
              placeholder="What worked? What didn't?"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-lg text-foreground font-medium hover:bg-muted transition-colors dark:border-slate-600 dark:hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium rounded-lg hover:shadow-lg disabled:opacity-50 transition-all"
            >
              {submitting ? 'Saving...' : 'Save Deal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RealWorldResults;
